import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import Header from './components/Header';
import Footer from './components/Footer';
import LandingPage from './components/LandingPage';
import AboutPage from './components/AboutPage';
import AllProductsPage from './components/AllProductsPage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import Orders from './components/Orders';
import Products from './components/Products';
import CategoryManagement from './components/CategoryManagement';
import CategoryPage from './components/CategoryPage';
import CreateOrder from './components/CreateOrder';
import AddProduct from './components/AddProduct';
import AddParty from './components/AddParty';
import RegisterUser from './components/RegisterUser';
import ProfilePage from './components/ProfilePage';
import AssignedDeliveries from './components/AssignedDeliveries';
import ViewParties from './components/ViewParties';
import RegisteredUsers from './components/RegisteredUsers';
import Statistics from './components/Statistics';
import CreatePurchaseOrder from './components/CreatePurchaseOrder';
import PurchaseOrders from './components/PurchaseOrders';
import StockTransfers from './components/StockTransfers';
import CreateStockTransfer from './components/CreateStockTransfer';
import StockMovementReport from './components/StockMovementReport';
import ReportsPage from './components/ReportsPage';
import ChangePassword from './components/ChangePassword';
import { setAuthToken, isAuthenticated } from './utils/api';
import { LocationProvider } from './contexts/LocationContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import NavigationSetter from './components/NavigationSetter';
import ScrollToTop from './components/ScrollToTop';

const PrivateRoute = ({ children, roles }) => {
    const location = useLocation();
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <Box className="flex justify-center items-center min-h-screen">
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return <Navigate to="/" state={{ from: location }} replace />;
    }

    if (roles && !roles.includes(user.role)) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

const Layout = ({ children, showHeader = true, showFooter = true }) => (
    <Box className="flex flex-col min-h-screen">
        {showHeader && <Header />}
        <Box className="flex-grow">{children}</Box>
        {showFooter && <Footer />}
    </Box>
);

function App() {
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            setAuthToken(token);
        }
    }, []);

    return (
        <ErrorBoundary>
            <AuthProvider>
                <LocationProvider>
                    <Router>
                        <NavigationProvider>
                            <NavigationSetter />
                            <ScrollToTop />
                            <Routes>
                            {/* Public Routes */}
                            <Route path="/" element={
                                <Layout showHeader={false} showFooter={false}>
                                    <LandingPage />
                                </Layout>
                            } />
                            <Route path="/about" element={
                                <Layout showHeader={false} showFooter={false}>
                                    <AboutPage />
                                </Layout>
                            } />
                            <Route path="/products" element={
                                <Layout showHeader={false} showFooter={false}>
                                    <AllProductsPage />
                                </Layout>
                            } />
                            <Route path="/category/:categoryId" element={
                                <Layout showHeader={false} showFooter={false}>
                                    <CategoryPage />
                                </Layout>
                            } />
                            <Route path="/login" element={
                                isAuthenticated() ?
                                <Navigate to="/dashboard" replace /> :
                                <Layout showHeader={false}><LoginPage /></Layout>
                            } />
                            <Route path="/change-password" element={
                                <Layout showHeader={true} showFooter={false}>
                                    <ChangePassword />
                                </Layout>
                            } />

                            {/* Private Routes */}
                            <Route path="/dashboard" element={
                                <Layout showHeader={false}>
                                    <PrivateRoute>
                                        <DashboardPage />
                                    </PrivateRoute>
                                </Layout>
                            } />
                            <Route path="/orders" element={
                                <Layout>
                                    <PrivateRoute roles={['owner', 'employee']}>
                                        <Orders />
                                    </PrivateRoute>
                                </Layout>
                            } />
                            <Route path="/manage-products" element={
                                <Layout>
                                    <PrivateRoute roles={['owner', 'employee']}>
                                        <Products />
                                    </PrivateRoute>
                                </Layout>
                            } />
                            <Route path="/categories" element={
                                <Layout>
                                    <PrivateRoute roles={['owner']}>
                                        <CategoryManagement />
                                    </PrivateRoute>
                                </Layout>
                            } />
                            <Route path="/create-order" element={
                                <Layout>
                                    <PrivateRoute roles={['owner', 'employee']}>
                                        <CreateOrder />
                                    </PrivateRoute>
                                </Layout>
                            } />
                            <Route path="/add-product" element={
                                <Layout>
                                    <PrivateRoute roles={['owner']}>
                                        <AddProduct />
                                    </PrivateRoute>
                                </Layout>
                            } />
                            <Route path="/add-party" element={
                                <Layout>
                                    <PrivateRoute roles={['owner']}>
                                        <AddParty />
                                    </PrivateRoute>
                                </Layout>
                            } />
                            <Route path="/register-user" element={
                                <Layout>
                                    <PrivateRoute roles={['owner']}>
                                        <RegisterUser />
                                    </PrivateRoute>
                                </Layout>
                            } />
                            <Route path="/profile" element={
                                <Layout>
                                    <PrivateRoute>
                                        <ProfilePage />
                                    </PrivateRoute>
                                </Layout>
                            } />
                            <Route path="/assigned-deliveries" element={
                                <Layout>
                                    <PrivateRoute roles={['delivery_person']}>
                                        <AssignedDeliveries />
                                    </PrivateRoute>
                                </Layout>
                            } />
                            <Route path="/view-parties" element={
                                <Layout>
                                    <PrivateRoute roles={['owner', 'employee']}>
                                        <ViewParties />
                                    </PrivateRoute>
                                </Layout>
                            } />
                            <Route path="/registered-users" element={
                                <Layout>
                                    <PrivateRoute roles={['owner']}>
                                        <RegisteredUsers />
                                    </PrivateRoute>
                                </Layout>
                            } />
                            <Route path="/statistics" element={
                                <Layout>
                                    <PrivateRoute roles={['owner']}>
                                        <Statistics />
                                    </PrivateRoute>
                                </Layout>
                            } />
                            <Route path="/create-purchase-order" element={
                                <Layout>
                                    <PrivateRoute roles={['owner', 'employee']}>
                                        <CreatePurchaseOrder />
                                    </PrivateRoute>
                                </Layout>
                            } />
                            <Route path="/purchase-orders" element={
                                <Layout>
                                    <PrivateRoute roles={['owner', 'employee']}>
                                        <PurchaseOrders />
                                    </PrivateRoute>
                                </Layout>
                            } />
                            <Route path="/stock-transfers" element={
                                <Layout>
                                    <PrivateRoute roles={['owner', 'employee']}>
                                        <StockTransfers />
                                    </PrivateRoute>
                                </Layout>
                            } />
                            <Route path="/create-stock-transfer" element={
                                <Layout>
                                    <PrivateRoute roles={['owner', 'employee']}>
                                        <CreateStockTransfer />
                                    </PrivateRoute>
                                </Layout>
                            } />
                            <Route path="/sales-report" element={
                                <Layout>
                                    <PrivateRoute roles={['owner', 'employee']}>
                                        <StockMovementReport />
                                    </PrivateRoute>
                                </Layout>
                            } />
                            <Route path="/reports" element={
                                <Layout>
                                    <PrivateRoute roles={['owner']}>
                                        <ReportsPage />
                                    </PrivateRoute>
                                </Layout>
                            } />

                            {/* Catch-all route */}
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </NavigationProvider>
                </Router>
            </LocationProvider>
            </AuthProvider>
        </ErrorBoundary>
    );
}

export default App;